import { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HubIcon from '@mui/icons-material/Hub';
import { useAuth } from '../store/auth';
import { avatarGradient } from '../lib/constants';

const NAV = [
  { to: '/', label: '首页' },
  { to: '/agents', label: '智能体' },
  { to: '/pricing', label: '套餐' },
];

export default function AppShell() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleLogout = async () => {
    closeMenu();
    await logout();
    navigate('/');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', backdropFilter: 'blur(8px)' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ gap: 1 }}>
            <RouterLink to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit' }}>
              <HubIcon color="primary" />
              <Typography fontWeight={800} fontSize={20} sx={{ letterSpacing: 0.5 }}>
                yingai
              </Typography>
            </RouterLink>

            <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
              {NAV.map((n) => {
                const active = location.pathname === n.to || (n.to !== '/' && location.pathname.startsWith(n.to));
                return (
                  <Button
                    key={n.to}
                    component={RouterLink}
                    to={n.to}
                    color={active ? 'primary' : 'inherit'}
                    sx={{ fontWeight: active ? 700 : 500 }}
                  >
                    {n.label}
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ flex: 1 }} />

            {isAuthenticated && user ? (
              <>
                <IconButton onClick={openMenu} size="small">
                  <Avatar
                    sx={{ width: 34, height: 34, fontSize: 14, background: avatarGradient(user.nickname || user.id) }}
                  >
                    {user.nickname?.[0]?.toUpperCase() ?? 'U'}
                  </Avatar>
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography fontWeight={700}>{user.nickname}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.planName ?? '免费版'}
                    </Typography>
                  </Box>
                  <MenuItem onClick={() => { closeMenu(); navigate('/me'); }}>
                    <PersonIcon fontSize="small" style={{ marginRight: 8 }} /> 我的 / 用量
                  </MenuItem>
                  <MenuItem onClick={() => { closeMenu(); navigate('/me/conversations'); }}>
                    <ChatBubbleOutlineIcon fontSize="small" style={{ marginRight: 8 }} /> 我的对话
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon fontSize="small" style={{ marginRight: 8 }} /> 退出登录
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button variant="contained" component={RouterLink} to="/login" size={isMobile ? 'small' : 'medium'}>
                登录
              </Button>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </Box>

      <Box component="footer" sx={{ borderTop: '1px solid', borderColor: 'divider', py: 3, mt: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            yingai · 让每个人，都用上对的 AI 智能体 · 演示环境数据仅本地有效
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
